// Reads each question aloud, in a real voice where one is available.
//
// WHY NOT JUST THE BROWSER
// SpeechSynthesis is free, instant and offline, and it sounds like a machine
// reading a list. For someone already nervous, being interviewed by a robot sets
// the wrong tone — and how at ease a candidate is feeds the confidence signals
// the interview scores. The voice is not decoration.
//
// So the server synthesises each question with a soft, warm TTS voice and hands
// back a URL. The browser voice stays as the fallback: worse, but instant and
// impossible to break.
//
// WHAT MAKES THIS FEEL FAST
// Server clips take 6-8 seconds to generate the first time anyone hears them.
// That never happens mid-interview because the server pre-warms the whole
// interview when the session starts, and twelve of sixteen questions are shared
// with every other candidate on the job and were generated long ago. This hook
// also fetches the NEXT question's audio while the current one plays, so the gap
// between questions is a network round trip rather than a synthesis.
//
// THE TRAP THIS AVOIDS
// If the voice is still speaking when the microphone opens, it lands in the
// recording and is transcribed as if the candidate said it — the interviewer's
// words reaching the scorer as the candidate's answer. Everything here stops
// unconditionally on cancel(), which the recording path calls before it starts.

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const STORAGE_KEY = "hiring.interview.voice";
const API = `${process.env.REACT_APP_API_BASE_URL}`.replace(/\/+$/, "");

function synth() {
  return typeof window !== "undefined" ? window.speechSynthesis : null;
}

// Voice lists load asynchronously and are empty on the first call in Chrome, so
// anything picking a voice has to wait for voiceschanged or it silently gets the
// platform default forever.
function useVoices() {
  const [voices, setVoices] = useState([]);
  useEffect(() => {
    const s = synth();
    if (!s) return undefined;
    const read = () => setVoices(s.getVoices() || []);
    read();
    s.addEventListener?.("voiceschanged", read);
    return () => s.removeEventListener?.("voiceschanged", read);
  }, []);
  return voices;
}

// Fallback voice selection. Prefers a female English voice, and en-IN first
// because the candidates are Indian and a familiar accent is easier to follow at
// speed — comprehension matters more here than the voice sounding impressive.
const FEMALE_HINT =
  /\b(female|woman|samantha|victoria|karen|moira|tessa|fiona|serena|zira|hazel|heera|swara|veena|google uk english female|aria|jenny|neerja)\b/i;

function pickVoice(voices) {
  if (!voices.length) return null;
  const english = voices.filter((v) => /^en(-|_|$)/i.test(v.lang || ""));
  if (!english.length) return null;

  const score = (v) => {
    let n = 0;
    if (FEMALE_HINT.test(v.name)) n += 50;
    if (/en[-_]IN/i.test(v.lang)) n += 30;
    else if (/en[-_]GB/i.test(v.lang)) n += 20;
    else if (/en[-_]US/i.test(v.lang)) n += 15;
    if (/google/i.test(v.name)) n += 12;
    if (/natural|neural|online/i.test(v.name)) n += 10;
    return n;
  };

  return english.slice().sort((a, b) => score(b) - score(a))[0] || english[0];
}

/**
 * Speaks interview questions.
 *
 * Returns `{ supported, enabled, setEnabled, speaking, loading, speak, cancel,
 * prefetch }`.
 *
 * The caller decides WHEN to speak. This hook deliberately does not watch the
 * question index itself — auto-speaking on render would fire again on any
 * unrelated re-render, and would talk over a candidate already recording.
 */
export function useQuestionSpeech({ token } = {}) {
  const voices = useVoices();
  const [speaking, setSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabledState] = useState(() => {
    if (typeof window === "undefined") return true;
    // Remembered per browser: a candidate who muted on question two should not
    // have the voice return on question three.
    return window.localStorage?.getItem(STORAGE_KEY) !== "off";
  });

  const supported =
    typeof window !== "undefined" &&
    ("speechSynthesis" in window || typeof Audio !== "undefined");

  const audioRef = useRef(null);
  const utteranceRef = useRef(null);
  // question index -> audio URL, so replaying and prefetching cost nothing.
  const cacheRef = useRef(new Map());
  const requestRef = useRef(0);

  const cancel = useCallback(() => {
    // Bumping the token invalidates any in-flight fetch: audio that arrives
    // after recording has started must never begin playing.
    requestRef.current += 1;

    const audio = audioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        /* already stopped */
      }
      audioRef.current = null;
    }

    const s = synth();
    if (s) {
      utteranceRef.current = null;
      try {
        s.cancel();
      } catch {
        /* already idle */
      }
    }

    setSpeaking(false);
    setLoading(false);
  }, []);

  const setEnabled = useCallback(
    (value) => {
      setEnabledState(value);
      try {
        window.localStorage?.setItem(STORAGE_KEY, value ? "on" : "off");
      } catch {
        /* private mode */
      }
      if (!value) cancel();
    },
    [cancel],
  );

  // The browser voice. Used when the server has no clip, and when synthesis is
  // unavailable entirely.
  const speakLocally = useCallback(
    (text) => {
      const s = synth();
      if (!s || !text) return;
      const utterance = new SpeechSynthesisUtterance(String(text));
      const voice = pickVoice(voices);
      if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang;
      } else {
        utterance.lang = "en-IN";
      }
      // Slightly under default and a touch higher: interview questions carry
      // qualifiers, and the default rate reads them faster than someone hearing
      // them for the first time can follow.
      utterance.rate = 0.94;
      utterance.pitch = 1.08;
      utterance.onend = () => {
        if (utteranceRef.current === utterance) setSpeaking(false);
      };
      utterance.onerror = () => {
        if (utteranceRef.current === utterance) setSpeaking(false);
      };
      utteranceRef.current = utterance;
      setSpeaking(true);
      try {
        s.speak(utterance);
      } catch {
        setSpeaking(false);
      }
    },
    [voices],
  );

  // ── Cached by identity, requested by position ─────────────────────────────
  //
  // `qid` is fixed for the life of a question; `index` is where it currently
  // sits. Those came apart the moment deepening began splicing questions in and
  // renumbering everything after them — and this cache, keyed on index, then
  // handed back the clip for whatever USED to be at that position. The screen
  // showed the follow-up question and Maya read the logistics one.
  //
  // So the key is the id and the request is the index: the server resolves the
  // index against the session as it is NOW, which is always current, while the
  // key describes a question rather than a seat.
  //
  // Falls back to the index for sessions that started before ids existed. Those
  // behave exactly as they did, which is the most that can be offered to an
  // interview already in flight.
  const keyFor = useCallback((index, qid) => qid || `i${index}`, []);

  const fetchAudioUrl = useCallback(
    async (index, qid) => {
      if (!token || index == null) return null;
      const key = keyFor(index, qid);
      if (cacheRef.current.has(key)) return cacheRef.current.get(key);
      try {
        const res = await axios.post(`${API}/api/hiring/public/interview/voice`, {
          token,
          question_index: index,
        });
        const url = res.data?.data?.audio_url || null;
        // Cached even when null, so a question the server cannot voice is not
        // re-requested on every replay.
        cacheRef.current.set(key, url);
        return url;
      } catch {
        cacheRef.current.set(key, null);
        return null;
      }
    },
    [token, keyFor],
  );

  /** Warms the next question's audio while the current one is being answered. */
  const prefetch = useCallback(
    (index, qid) => {
      if (!enabled || index == null) return;
      fetchAudioUrl(index, qid);
    },
    [enabled, fetchAudioUrl],
  );

  const speak = useCallback(
    async (text, index, qid) => {
      if (!enabled || !text) return;
      cancel();

      const ticket = requestRef.current;
      setLoading(true);

      const url = await fetchAudioUrl(index, qid);

      // cancel() ran while we were waiting — recording has probably started.
      if (ticket !== requestRef.current) return;
      setLoading(false);

      if (!url) {
        speakLocally(text);
        return;
      }

      try {
        const audio = new Audio(url);
        audio.onended = () => {
          if (audioRef.current === audio) setSpeaking(false);
        };
        audio.onerror = () => {
          // The clip exists but will not play — expired URL, codec, offline.
          // Reading it in the browser voice is better than silence.
          if (audioRef.current === audio) {
            setSpeaking(false);
            speakLocally(text);
          }
        };
        audioRef.current = audio;
        setSpeaking(true);
        await audio.play();
      } catch {
        // Autoplay policy, usually. The candidate has already clicked to get
        // here, so this is rare — but silence would be worse than a robot.
        setSpeaking(false);
        speakLocally(text);
      }
    },
    [enabled, cancel, fetchAudioUrl, speakLocally],
  );

  // Chrome keeps an utterance queue alive across navigation within the SPA, and
  // an <audio> element keeps playing after its component is gone.
  useEffect(() => cancel, [cancel]);

  return { supported, enabled, setEnabled, speaking, loading, speak, cancel, prefetch };
}

export default useQuestionSpeech;
